import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["gates"];
import { useCallback, useEffect, useMemo, useState } from 'react';
import { RouterProvider } from 'quartz-routing';
import Raven from 'raven-js';
import { getAuthResult } from 'quartz-auth/service';
import { GraniteRouter } from './GraniteRouter';
import { isGraniteEnabledForCurrentApp, getUngatedRemoteRoutes } from './granite-config';
import { getHostHistory } from 'quartz-routing/initialize';
import { activateApp } from './activateApp';
import { GlobalErrorBoundary } from '../components/GlobalErrorBoundary';
import { ErrorPage } from '../components/ErrorPage';
import { AppRoot } from '../internal/root-renderer';
import { isPromiseLike } from '../internal/isPromiseLike';
import { getCurrentPackageName } from 'quartz/framework/environment/service';
export { isGraniteEnabledForCurrentApp };
import { getQuartzConfig } from 'quartz-config/evaluable';
import { getBasenameFromRouting } from '../util/getBasenameFromRouting';
import { getGraniteRuntime } from './GraniteRuntime';
import { jsx as _jsx } from "react/jsx-runtime";
function LocalApp({
  history,
  activationResult,
  AppComponent,
  LayoutComponent,
  rhumbConfig,
  rhumbTrackVisibility,
  userId,
  softNavigationContext
}) {
  const [result, setResult] = useState(null);
  useEffect(() => {
    function handleResult(r) {
      if (r && 'redirect' in r) {
        window.location.href = r.redirect;
        return;
      }
      setResult(r);
    }
    if (isPromiseLike(activationResult)) {
      activationResult.then(handleResult).catch(error => {
        Raven.captureException(error);
        setResult(null);
      });
    } else {
      handleResult(activationResult);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (result && 'onAfterRender' in result) {
      var _result$onAfterRender;
      (_result$onAfterRender = result.onAfterRender) === null || _result$onAfterRender === void 0 || _result$onAfterRender.call(result);
    }
  }, [result]);
  if (result === null) {
    return null;
  }
  const content = LayoutComponent ? /*#__PURE__*/_jsx(LayoutComponent, {
    children: /*#__PURE__*/_jsx(AppComponent, Object.assign({}, result === null || result === void 0 ? void 0 : result.props))
  }) : /*#__PURE__*/_jsx(AppComponent, Object.assign({}, result === null || result === void 0 ? void 0 : result.props));
  return /*#__PURE__*/_jsx(AppRoot, {
    history: history,
    rhumbConfig: rhumbConfig,
    rhumbTrackVisibility: rhumbTrackVisibility,
    userId: userId,
    softNavigationContext: softNavigationContext,
    isGraniteEnabled: true,
    children: content
  });
}
function LocalRouteContent({
  adapter,
  prepareApp,
  copilotLayoutType,
  AppComponent,
  LayoutComponent,
  rhumbConfig,
  rhumbTrackVisibility,
  userId
}) {
  const runtime = getGraniteRuntime();
  const packageName = getCurrentPackageName();
  const quartzConfig = getQuartzConfig();
  const historyBasename = /* quartzConfig.type === 'application' */true ? quartzConfig.historyBasename || getBasenameFromRouting(quartzConfig.UNSAFE_EXPERIMENTAL_routing) : null;
  if (!historyBasename) {
    throw new Error('historyBasename is required for granite local app');
  }
  const [{
    history,
    softNavigationContext,
    appResult
  }] = useState(() => runtime.activateLocal(packageName, historyBasename, {
    copilotLayoutType,
    initApp: () => activateApp(adapter, getAuthResult(), prepareApp)
  }));
  return /*#__PURE__*/_jsx(LocalApp, {
    activationResult: appResult,
    history: history,
    softNavigationContext: softNavigationContext,
    AppComponent: AppComponent,
    LayoutComponent: LayoutComponent,
    rhumbConfig: rhumbConfig,
    rhumbTrackVisibility: rhumbTrackVisibility,
    userId: userId
  });
}
export function GraniteLocalApp(_ref) {
  let {
      gates
    } = _ref,
    localAppProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    remoteModules,
    localRouteOverrides
  } = useMemo(() => getUngatedRemoteRoutes(gates), [gates]);
  const getRoutes = useCallback(() => ({
    remoteModules,
    localRouteOverrides
  }), [remoteModules, localRouteOverrides]);
  return /*#__PURE__*/_jsx(GlobalErrorBoundary, {
    ErrorComponent: ErrorPage,
    children: /*#__PURE__*/_jsx(RouterProvider, {
      history: getHostHistory(),
      children: /*#__PURE__*/_jsx(GraniteRouter, {
        getRoutes: getRoutes,
        children: /*#__PURE__*/_jsx(LocalRouteContent, Object.assign({}, localAppProps))
      })
    })
  });
}